import {getChannel, getChannelMember, joinChannel, getChannelStats} from 'mattermost-redux/actions/channels';
import {getPostThread} from 'mattermost-redux/actions/posts';
import {getCurrentTeam, getTeam} from 'mattermost-redux/selectors/entities/teams';
import {getCurrentUser, getCurrentUserId} from 'mattermost-redux/selectors/entities/users';
import {getCurrentChannel} from 'mattermost-redux/selectors/entities/channels';
import {getUserIdFromChannelName} from 'mattermost-redux/utils/channel_utils';

import {loadChannelsForCurrentUser} from 'actions/channel_actions';
import {loadNewDMIfNeeded, loadNewGMIfNeeded} from 'actions/user_actions';
import {selectPostAndHighlight} from 'actions/views/rhs';
import {setEditingPost} from 'actions/post_actions';

import {browserHistory} from 'utils/browser_history';
import {joinPrivateChannelPrompt} from 'utils/channel_utils';
import {ActionTypes, ErrorPageTypes} from 'utils/constants';
import {isSystemAdmin} from 'mattermost-redux/utils/user_utils';
import {isComment, getPostURL} from 'utils/post_utils';
import {selectChannel} from 'features/channels/actions/select_channel';
import {fetchUsersByIds} from 'features/users/actions/fetch_users_by_ids';
import {readOnlyChannelsSlice} from 'features/channels';
import {reportErrorToSentry} from 'utils/sentry';

import * as Utils from 'utils/utils';
import {isDirectChannel} from 'utils/isDirectChannel';
import {isGroupChannel} from 'utils/isGroupChannel';
import {isOpenChannel} from 'utils/isOpenChannel';
import {isPrivateChannel} from 'utils/isPrivateChannel';

let privateChannelJoinPromptVisible = false;

function reportPermalinkNotFoundError(params) {
    const messageDetails = {reason: params.reason, url: window.location.href};

    if (params.error) {
        messageDetails.errorCode = params.error.code || params.error.status_code;
        messageDetails.errorMessage = params.error.originalMessage || params.error.message;
    }

    reportErrorToSentry(new Error('permalink not found'), {extra: messageDetails});
}

function redirectToPermalinkNotFoundPage(returnTo = '') {
    browserHistory.replace(`/error?type=${ErrorPageTypes.PERMALINK_NOT_FOUND}&returnTo=${returnTo}`);
}

function focusRootPost(post, channel) {
    return async (dispatch, getState) => {
        const postURL = getPostURL(getState(), post);

        dispatch(selectChannel(channel.id));
        dispatch({
            type: ActionTypes.RECEIVED_FOCUSED_POST,
            data: post.id,
            channelId: channel.id,
        });

        if (!postURL) {
            reportErrorToSentry(new Error('Failed to generate the post url'));
        }

        browserHistory.replace(postURL);
    };
}

function focusReplyPost(post, channel, teamId, returnTo) {
    return async (dispatch, getState) => {
        await dispatch(getPostThread(post.root_id));
        const state = getState();

        const team = getTeam(state, channel.team_id || teamId);
        const currentChannel = getCurrentChannel(state);
        const sameTeam = currentChannel && currentChannel.team_id === team?.id;

        if (!sameTeam) {
            dispatch(selectChannel(channel.id));
        }

        if (sameTeam && returnTo) {
            browserHistory.replace(returnTo);
        } else {
            const postURL = getPostURL(state, post);
            if (!postURL) {
                reportErrorToSentry(new Error('Failed to generate the post url'));
                return;
            }
            browserHistory.replace(postURL);
        }

        dispatch(selectPostAndHighlight(post));
    };
}

export function focusPost(postId, returnTo = '', currentUserId) {
    return async (dispatch, getState) => {
        // Ignore if prompt is still visible
        if (privateChannelJoinPromptVisible) {
            return;
        }
        const {data, error} = await dispatch(getPostThread(postId));

        if (!data) {
            reportPermalinkNotFoundError({reason: 'failed to fetch thread', error});
            redirectToPermalinkNotFoundPage(returnTo);
            return;
        }

        const state = getState();

        const channelId = data.posts[data.order[0]].channel_id;
        let channel = state.entities.channels.channels[channelId];
        const currentTeam = getCurrentTeam(state);
        const teamId = currentTeam.id;

        if (!channel) {
            const {data: channelData, error: channelError} = await dispatch(getChannel(channelId));

            if (!channelData) {
                reportPermalinkNotFoundError({reason: 'failed to fetch channel', error: channelError});
                redirectToPermalinkNotFoundPage(returnTo);
                return;
            }

            channel = channelData;
        }

        let myMember = state.entities.channels.myMembers[channelId];

        if (!myMember) {
            const membership = await dispatch(getChannelMember(channel.id, currentUserId));

            if ('data' in membership) {
                myMember = membership.data;
            } else if (isOpenChannel(channel)) {
                dispatch(readOnlyChannelsSlice.actions.addChannel(channel.id));
            }

            if (!myMember) {
                // If it's a DM or GM channel and we don't have a channel member for it already, user is not a member
                if (isDirectChannel(channel) || isGroupChannel(channel)) {
                    reportPermalinkNotFoundError({reason: 'not a member of DM or GM channel'});
                    redirectToPermalinkNotFoundPage(returnTo);
                    return;
                }

                // Prompt system admin before joining the private channel
                const user = getCurrentUser(state);
                if (isPrivateChannel(channel) && isSystemAdmin(user.roles)) {
                    privateChannelJoinPromptVisible = true;
                    const joinPromptResult = await dispatch(joinPrivateChannelPrompt(currentTeam, channel));
                    privateChannelJoinPromptVisible = false;
                    if ('data' in joinPromptResult && !joinPromptResult.data.join) {
                        return;
                    }
                    await dispatch(joinChannel(currentUserId, null, channelId));
                }
            }
        }

        if (channel.team_id && channel.team_id !== teamId) {
            reportPermalinkNotFoundError({reason: 'failed to switch team'});
            redirectToPermalinkNotFoundPage(returnTo);
            return;
        }

        if (isDirectChannel(channel)) {
            const userId = getUserIdFromChannelName(currentUserId, channel.name);
            await dispatch(fetchUsersByIds({
                userIds: [userId],
            }));
            dispatch(loadNewDMIfNeeded(channel.id));
        } else if (isGroupChannel(channel)) {
            dispatch(loadNewGMIfNeeded(channel.id));
        }

        const post = data.posts[postId];

        if (isComment(post)) {
            dispatch(focusReplyPost(post, channel, teamId, returnTo));
        } else {
            dispatch(focusRootPost(post, channel));
        }

        dispatch(loadChannelsForCurrentUser());
        dispatch(getChannelStats(channelId));
    };
}

export function focusPostToEdit(postId, returnTo = '') {
    return async (dispatch, getState) => {
        const {data, error} = await dispatch(getPostThread(postId));

        if (!data) {
            reportPermalinkNotFoundError({reason: 'failed to fetch thread', error});
            redirectToPermalinkNotFoundPage(returnTo);
            return;
        }

        const state = getState();

        const channelId = data.posts[data.order[0]].channel_id;
        let channel = state.entities.channels.channels[channelId];

        if (!channel) {
            const {data: channelData, error: channelError} = await dispatch(getChannel(channelId));

            if (!channelData) {
                reportPermalinkNotFoundError({reason: 'failed to fetch channel', error: channelError});
                redirectToPermalinkNotFoundPage(returnTo);
                return;
            }

            channel = channelData;
        }

        const teamId = channel.team_id;

        if (isDirectChannel(channel)) {
            const currentUserId = getCurrentUserId(state);
            const userId = getUserIdFromChannelName(currentUserId, channel.name);
            await dispatch(fetchUsersByIds({
                userIds: [userId],
            }));
            dispatch(loadNewDMIfNeeded(channel.id));
        } else if (isGroupChannel(channel)) {
            dispatch(loadNewGMIfNeeded(channel.id));
        }

        const post = data.posts[postId];

        if (isComment(post)) {
            await dispatch(focusReplyPost(post, channel, teamId, returnTo));
        } else {
            await dispatch(focusRootPost(post, channel));
        }

        const isThreadPost = isComment(post);

        dispatch(setEditingPost(
            post.id,
            isThreadPost ? 'create_comment' : 'create_post',
            isThreadPost ? Utils.localizeMessage('rhs_comment.comment', 'Comment') : Utils.localizeMessage('create_post.post', 'Post'),
            isThreadPost,
        ));
    };
}
